import { AppFeedback } from 'components/appFeedback/AppFeedback';
import { ActionButton, ButtonPanel, MessagesPanel } from 'components/secondaryMarket/shared/status';
import { ErrorItem } from 'components/secondaryMarket/shared/status/messages/ErrorItem';
import { PanelFooter, PanelHeader, PanelTitle } from 'components/secondaryMarket/shared/status/Panel';
import { useNavigateTo } from 'hooks/useNavigateTo';
import { useIntl } from 'react-intl';
import { logAnalytics } from 'shared/middlewares/analytics/logger';
import type { Form } from 'state/secondaryMarket/shared/form/form.model';
import type { SecondaryMarketFormError } from 'state/secondaryMarket/shared/form/validationResults';

import type { SecondaryMarketPostEventTabType } from '../../../../../state/workspaces/model';
import { moreAgressiveColorMapper } from '../../../../PostTrade/ProductModification/components/ValidationErrors.utils';

export type DoneAmendSidePanelProps<F = Form> = Readonly<{
  tabType: SecondaryMarketPostEventTabType;
  validationErrors: readonly SecondaryMarketFormError<F>[];
  onDone: () => void;
  onNavigateToOrdersBlotter: () => void;
}>;
export function DoneAmendSidePanel<F = Form>({
  tabType,
  onDone,
  onNavigateToOrdersBlotter,
  validationErrors,
}: DoneAmendSidePanelProps<F>) {
  const { formatMessage } = useIntl();
  const navigateToOrderBlotter = useNavigateTo('/secondary-orders-blotter');
  return (
    <div className="d-flex flex-column h-100 justify-content-start overflow-y-auto">
      <PanelHeader color="info" discreet>
        <PanelTitle className="flex-grow-1">
          {tabType === 'live'
            ? formatMessage({ id: 'secondaryMarket.sidePanel.live.label.appliedDone' })
            : formatMessage({ id: 'secondaryMarket.sidePanel.cpOrder.label.appliedDone' })}
        </PanelTitle>
      </PanelHeader>
      <PanelFooter
        color="info"
        statusKey={
          tabType === 'live'
            ? 'secondaryMarket.sidePanel.live.label.appliedDoneStatus'
            : 'secondaryMarket.sidePanel.cpOrder.label.appliedDoneStatus'
        }
        icon="check_circle"
      />
      <div className="flex-grow-1 overflow-y-auto w-100 pt-2">
        <MessagesPanel
          errors={validationErrors}
          e2e="side-panel-header-validations"
          colorToSeverity={moreAgressiveColorMapper}
        />
        <ErrorItem
          index={0}
          htmlMessage={formatMessage({ id: 'secondaryMarket.sidePanel.label.appliedDoneMessage' })}
          severity="info"
        />
      </div>
      <ButtonPanel>
        <AppFeedback
          withGuidedTour={false}
          Button={({ toggle }) => (
            <ActionButton
              onClick={() => {
                toggle();
                logAnalytics.trackEvent('Secondary market', 'Open order feedback modal');
              }}
              label="secondaryMarket.sidePanel.label.pricingFeedbackMessage"
              isFlat
              color="secondary"
              icon="reviews"
            />
          )}
        ></AppFeedback>
        <div className="btn-group my-1 w-100">
          {onDone && (
            <ActionButton
              color="discreet-secondary"
              label="secondaryMarket.sidePanel.label.ok"
              e2e="closeOrderButton"
              className="w-50"
              disabled={false}
              onClick={onDone}
            />
          )}
          <ActionButton
            color="default"
            label="secondaryMarket.sidePanel.label.viewInBlotter"
            e2e="viewTradeBlotterButton"
            className="w-50"
            disabled={false}
            onClick={() => {
              navigateToOrderBlotter();
              onNavigateToOrdersBlotter();
              logAnalytics.trackEvent('Secondary market', 'View in trade blotter');
            }}
          />
        </div>
      </ButtonPanel>
    </div>
  );
}
DoneAmendSidePanel.displayName = 'DoneAmendSidePanel';
